// ** React Imports
import { useState, useEffect, MouseEvent, useCallback, ReactElement } from 'react'

// ** Next Import
import { Link } from 'react-router-dom'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Menu from '@mui/material/Menu'
import Grid from '@mui/material/Grid'
import { DataGrid } from '@mui/x-data-grid'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import CardHeader from '@mui/material/CardHeader'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import CardContent from '@mui/material/CardContent'
import Select, { SelectChangeEvent } from '@mui/material/Select'

// ** Icons Imports
import Laptop from 'mdi-material-ui/Laptop'
import ChartDonut from 'mdi-material-ui/ChartDonut'
import CogOutline from 'mdi-material-ui/CogOutline'
import EyeOutline from 'mdi-material-ui/EyeOutline'
import DotsVertical from 'mdi-material-ui/DotsVertical'
import PencilOutline from 'mdi-material-ui/PencilOutline'
import DeleteOutline from 'mdi-material-ui/DeleteOutline'
import AccountOutline from 'mdi-material-ui/AccountOutline'

// ** Store Imports
import { SelectOption } from 'src/types/common';


// ** Custom Components Imports
import CustomChip from 'src/materio/@core/components/mui/chip'
import CustomAvatar from 'src/materio/@core/components/mui/avatar'

// ** Utils Import
import { getInitials } from 'src/materio/@core/utils/get-initials'

// ** Actions Imports


// ** Types Imports

import { ThemeColor } from 'src/materio/@core/layouts/types'
/*import { UsersType } from 'src/types/apps/userTypes'*/

// ** Custom Components Imports
import TableHeader from 'src/materio/views/apps/user/list/TableHeader'
import AddUserDrawer from 'src/materio/views/apps/user/list/AddUserDrawer'
import { client } from 'src/utils/axios-client'
import { adminChangePasswordEndpoint, getUsersEndpoint, roleOptionsApiEndpoint, sendActivationEndpoint, toggleUserActiveEndpoint } from 'src/configs/endpoints'
import { PagedList, UserOverview } from 'src/types/common'
import DoneIcon from '@mui/icons-material/Done';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import UserStore from '../../../stores/UserStore'
import SendIcon from '@mui/icons-material/Send';
import PasswordIcon from '@mui/icons-material/Password';
import LockResetIcon from '@mui/icons-material/LockReset';
import GroupIcon from '@mui/icons-material/Group';
import CancelIcon from '@mui/icons-material/Cancel';

import _ from 'lodash'
import { Snackbar, TextField } from '@mui/material'
import ConfirmationDialog from '../../common/ConfirmationDialog'
import ApplicationStore from '../../../stores/ApplicationStore'
import RenderIf, { ClaimType, ClaimValue } from '../../common/RenderIf'
interface UserRoleType {
    [key: string]: ReactElement
}

interface UserStatusType {
    [key: string]: ThemeColor
}

// ** Vars
const userRoleObj: UserRoleType = {
    admin: <Laptop fontSize='small' sx={{ mr: 3, color: 'error.main' }} />,
    author: <CogOutline fontSize='small' sx={{ mr: 3, color: 'warning.main' }} />,
    editor: <PencilOutline fontSize='small' sx={{ mr: 3, color: 'info.main' }} />,
    maintainer: <ChartDonut fontSize='small' sx={{ mr: 3, color: 'success.main' }} />,
    subscriber: <AccountOutline fontSize='small' sx={{ mr: 3, color: 'primary.main' }} />
}

interface CellType {
    row: UserOverview
}

const userStatusObj: UserStatusType = {
    active: 'success',
    pending: 'warning',
    inactive: 'secondary'
}

// ** Styled component for the link for the avatar with image
const AvatarWithImageLink = styled(Link)(({ theme }) => ({
    marginRight: theme.spacing(3)
}))

// ** Styled component for the link for the avatar without image
const AvatarWithoutImageLink = styled(Link)(({ theme }) => ({
    textDecoration: 'none',
    marginRight: theme.spacing(3)
}))

// ** renders client column
const renderClient = (row: UserOverview) => {
    if (row.avatar && row.avatar.length) {
        return (
            <AvatarWithImageLink to={`/apps/user/view/${row.id}`}>
                <CustomAvatar src={row.avatar} sx={{ mr: 3, width: 30, height: 30 }} />
            </AvatarWithImageLink>
        )
    } else {
        return (
            <AvatarWithoutImageLink to={`/apps/user/view/${row.id}`}>
                <CustomAvatar
                    skin='light'
                    color={'primary'}
                    sx={{ mr: 3, width: 30, height: 30, fontSize: '.875rem' }}
                >
                    {getInitials(row.fullName ? row.fullName : 'John Doe')}
                </CustomAvatar>
            </AvatarWithoutImageLink>
        )
    }
}

// ** Styled component for the link inside menu
const MenuItemLink = styled('a')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    padding: theme.spacing(1.5, 4),
    color: theme.palette.text.primary
}))

const RowOptions = ({ id, status }: { id: number | string, status: string }) => {
    // ** Hooks  

    // ** State
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState(false);
    const [emilActivationOpen, setEmailActivationOpen] = useState(false);
    const rowOptionsOpen = Boolean(anchorEl)

    const handleRowOptionsClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleRowOptionsClose = () => {
        setAnchorEl(null)
    }

    const toggleActive = async () => {
        const message = status !== 'inactive' ? "Are you sure you want to deactivate the user?" : 'Are you sure you want to activate the user?';

        const result = confirm(message)

        if (result) {
            await client.post(toggleUserActiveEndpoint.replace('{id}', id.toString()), { userId: id });

            UserStore.update((s, o) => {
                const clone = _.cloneDeep(o.users);

                const user = clone.find(s => s.id == id);

                if (user) {
                    if (user.status === "active") {
                        user.status = 'inactive';
                    } else {
                        user.status = 'active';
                    }
                }

                s.users = clone;
            })
        }
        /*        dispatch(deleteUser(id))*/
        handleRowOptionsClose()
    }

    const closeChangePassword = () => {
        setPassword('');
        setPasswordError(false);
        handleRowOptionsClose();
    }

    const changePassword = async () => {
        setPasswordError(false);

        if (_.isEmpty(password)) {
            setPasswordError(true);
            return;
        }

        try {
            const response = await client.post(adminChangePasswordEndpoint, {
                id,
                password
            });

            if (!response.data.succeeded) {
                setPasswordError(true);
                return;
            }

            closeChangePassword();
        }
        catch (err) {

        }
    }

    const sendEmailActivation = async () => {
        try {
            var response = await client.post(sendActivationEndpoint, { id });

            if (response.data.succeeded) {
                ApplicationStore.update(s => {
                    s.snackbarMessage = 'Activation Email Sent';
                    s.snackbarOpen = true;
                })
            } else {
                ApplicationStore.update(s => {
                    s.snackbarMessage = 'Error occured sending activation email';
                    s.snackbarOpen = true;
                })
            }
        } catch (err) {
            ApplicationStore.update(s => {
                s.snackbarMessage = 'Error occured sending activation email';
                s.snackbarOpen = true;
            })
        }

        handleRowOptionsClose();
    }

    return (
        <>
            <IconButton size='small' onClick={handleRowOptionsClick}>
                <DotsVertical />
            </IconButton>
            <Menu
                keepMounted
                anchorEl={anchorEl}
                open={rowOptionsOpen}
                onClose={handleRowOptionsClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                PaperProps={{ style: { minWidth: '8rem' } }}
            >
               
                {status === 'inactive' &&
                     <RenderIf claimType={ClaimType.Users} claimValue={ClaimValue.ActivateUser}>
                        <MenuItem onClick={toggleActive}>
                            <DoneIcon fontSize='small' sx={{ mr: 2 }} />
                                Activate
                        </MenuItem>
                    </RenderIf>
                }
                
                <RenderIf claimType={ClaimType.Users} claimValue={ClaimValue.DeactivateUser}>
                    {status !== 'inactive' &&
                        <MenuItem onClick={toggleActive}>
                            <DoNotDisturbIcon fontSize='small' sx={{ mr: 2 }} />
                            Deactivate
                        </MenuItem>}
                </RenderIf>
                <RenderIf claimType={ClaimType.Users} claimValue={ClaimValue.ChangePassword}>
                    <MenuItem onClick={() => setChangePasswordOpen(true)}>
                        <LockResetIcon fontSize='small' sx={{ mr: 2 }} />
                        {!changePasswordOpen && <span>Change Password</span>}
                        {changePasswordOpen && <div><TextField error={passwordError} label="Password" value={password} onChange={(e) => setPassword(e.target.value)} /><CancelIcon onClick={closeChangePassword} /><DoneIcon onClick={changePassword} /></div>}
                    </MenuItem>  
                </RenderIf>
                <RenderIf claimType={ClaimType.Users} claimValue={ClaimValue.SendActivationEmail}>
                    <MenuItem onClick={sendEmailActivation}>
                        <SendIcon fontSize='small' sx={{ mr: 2 }} />
                        Send Activation Email
                    </MenuItem>
                </RenderIf>             
                
            </Menu>
        </>
    )
}

const columns = [
    {
        flex: 0.2,
        minWidth: 230,
        field: 'fullName',
        headerName: 'User',
        renderCell: ({ row }: CellType) => {
            const { id, fullName, email } = row

            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {renderClient(row)}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                        <Link to={`/apps/user/view/${id}`}>
                            <Typography
                                noWrap
                                component='a'
                                variant='body2'
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {fullName}
                            </Typography>
                        </Link>
                        <Link to={`/apps/user/view/${id}`}>
                            <Typography noWrap component='a' variant='caption' sx={{ textDecoration: 'none' }}>
                                {email}
                            </Typography>
                        </Link>
                    </Box>
                </Box>
            )
        }
    },
    {
        flex: 0.2,
        minWidth: 250,
        field: 'email',
        headerName: 'Email',
        renderCell: ({ row }: CellType) => {
            return (
                <Typography noWrap variant='body2'>
                    {row.email}
                </Typography>
            )
        }
    },
    {
        flex: 0.15,
        field: 'role',
        minWidth: 150,
        headerName: 'Active Role',
        renderCell: ({ row }: CellType) => {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {userRoleObj[row.roleName]}
                    <Typography noWrap sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                        {row.roleName}
                    </Typography>
                </Box>
            )
        }
    },
    {
        flex: 0.1,
        minWidth: 110,
        field: 'status',
        headerName: 'Status',
        renderCell: ({ row }: CellType) => {
            return (
                <CustomChip
                    skin='light'
                    size='small'
                    label={row.status}
                    color={userStatusObj[row.status]}
                    sx={{ textTransform: 'capitalize' }}
                />
            )
        }
    },
    {
        flex: 0.1,
        minWidth: 90,
        sortable: false,
        field: 'actions',
        headerName: 'Actions',
        renderCell: ({ row }: CellType) => <RowOptions id={row.id} status={row.status} />
    }
]

const UserList = () => {
    // ** State
    const [roleId, setRoleId] = useState<string>('')
    const [pageSize, setPageSize] = useState(10);
    const [value, setValue] = useState<string>('')
    const [status, setStatus] = useState<string>('')
    const [addUserOpen, setAddUserOpen] = useState<boolean>(false)
    const [roles, setRoles] = useState([] as SelectOption[])
    const list = UserStore.useState(s => s.users);
    const [filteredList, setFilteredList] = useState([] as UserOverview[])
    // ** Hooks

    useEffect(() => {
        loadRecords(1, pageSize);
    }, [status, roleId]);

    useEffect(() => {
        const loadRoles = async () => {
            try {
                var response = await client.get(roleOptionsApiEndpoint);
                setRoles(response.data);
            }
            catch (err) {
                console.log(err)
            }

        }

        loadRoles();
    }, [])

    useEffect(() => {
        setFilteredList(list.filter(u => value == '' || (u.email.toUpperCase().includes(value.toUpperCase()) || u.fullName.toUpperCase().includes(value.toUpperCase()))));
    }, [list, value])

    const loadRecords = async (pageNumber: number, pageSize: number) => {
        const response = await client.get<UserOverview[]>(getUsersEndpoint, { params: { pageNumber, pageSize, roleId, status } });

        UserStore.update(s => {
            s.users = response.data;
        })
    }

    //const onPageChange = (newPage: number) => {
    //    loadRecords(newPage, list.pageSize);
    //}

    const handleFilter = useCallback((val: string) => {
        setValue(val)
    }, [])

    const handleRoleChange = useCallback((e: SelectChangeEvent) => {
        setRoleId(e.target.value)
    }, [])

    const handleStatusChange = useCallback((e: SelectChangeEvent) => {
        setStatus(e.target.value)
    }, [])

    const toggleAddUserDrawer = async () => {
        if (addUserOpen) {
            await loadRecords(1, pageSize);
        }
        setAddUserOpen(!addUserOpen)
    }

    return (
        <Grid container spacing={6}>
            <Grid item xs={12}>
                <Card>
                    <CardHeader title='Search Filters' />
                    <CardContent>
                        <Grid container spacing={6}>
                            <Grid item sm={4} xs={12}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id='role-select'>Select Role</InputLabel>
                                    <Select
                                        fullWidth
                                        value={roleId}
                                        id='select-role'
                                        label='Select Role'
                                        labelId='role-select'
                                        onChange={handleRoleChange}
                                        inputProps={{ placeholder: 'Select Role' }}
                                    >
                                        <MenuItem value=''>Select Role</MenuItem>
                                        {roles.map(role => <MenuItem value={role.value}>{role.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item sm={4} xs={12}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id='status-select'>Select Status</InputLabel>
                                    <Select
                                        fullWidth
                                        value={status}
                                        id='select-status'
                                        label='Select Status'
                                        labelId='status-select'
                                        onChange={handleStatusChange}
                                        inputProps={{ placeholder: 'Select Role' }}
                                    >
                                        <MenuItem value=''>Select Role</MenuItem>
                                        <MenuItem value='pending'>Pending</MenuItem>
                                        <MenuItem value='active'>Active</MenuItem>
                                        <MenuItem value='inactive'>Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12}>
                <Card>
                    <TableHeader value={value} handleFilter={handleFilter} toggle={toggleAddUserDrawer} />
                    <DataGrid
                        autoHeight
                        pagination
                        rows={filteredList}
                        columns={columns}
                        pageSize={pageSize}
                        disableSelectionOnClick
                        rowsPerPageOptions={[10, 25, 50]}
                        onPageSizeChange={(newPageSize: number) => setPageSize(newPageSize)}
                    />
                </Card>
            </Grid>

            <AddUserDrawer open={addUserOpen} toggle={toggleAddUserDrawer} />
        </Grid>
    )
}

export { UserList }
export default UserList
